import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FundingMatchCard from '../../components/funding/FundingMatchCard';
import FundingMatchClientSelector from '../../components/funding/FundingMatchClientSelector';
import FundingMatchFilters from '../../components/funding/FundingMatchFilters';
import FundingMatchRunPanel from '../../components/funding/FundingMatchRunPanel';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingAIAdvisorActionLink from '../../components/funding/FundingAIAdvisorActionLink';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingMatch } from '../../hooks/funding/useFundingMatch';
import { addMatchToSequence } from '../../services/funding/fundingSequenceService';
import { createSubmissionFromMatch } from '../../services/funding/fundingSubmissionsService';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const FundingBankMatchPage = () => {
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [lenderType, setLenderType] = useState('');
  const [productType, setProductType] = useState('');
  const [fitTier, setFitTier] = useState('');

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  const agencyIdForClient = selectedClient?.agencyId || workspaceAgencyId || undefined;

  const {
    matches,
    loading: matchesLoading,
    running,
    error: matchError,
    run,
    load,
  } = useFundingMatch({
    clientId: selectedClientId,
    agencyId: agencyIdForClient,
    filters: {
      fitTier: fitTier || undefined,
      lenderType: lenderType || undefined,
      productType: productType || undefined,
    },
    autoLoad: Boolean(selectedClientId && agencyIdForClient),
  });

  const loadClients = useCallback(async () => {
    if (!isPlatformContext && !workspaceAgencyId) {
      setClients([]);
      setClientsLoading(false);
      return;
    }

    setClientsLoading(true);
    try {
      const result = await fetchFundingClients({
        agencyId: isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId,
      });
      setClients(result.clients);
      if (!selectedClientId && result.clients.length > 0) {
        setSelectedClientId(result.clients[0].id);
      }
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [isPlatformContext, workspaceAgencyId, selectedClientId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const [addingMatchId, setAddingMatchId] = useState('');
  const [creatingSubmissionMatchId, setCreatingSubmissionMatchId] = useState('');

  const handleAddToSequence = async (match) => {
    if (!agencyIdForClient) return null;
    setAddingMatchId(match.id);
    try {
      return await addMatchToSequence(match.id, agencyIdForClient);
    } finally {
      setAddingMatchId('');
    }
  };

  const handleCreateSubmission = async (match) => {
    if (!agencyIdForClient) return null;
    setCreatingSubmissionMatchId(match.id);
    try {
      return await createSubmissionFromMatch(match.id, agencyIdForClient);
    } finally {
      setCreatingSubmissionMatchId('');
    }
  };

  const handleRunMatch = async () => {
    if (!selectedClientId || !agencyIdForClient) return;
    try {
      await run();
      await load();
    } catch {
      // surfaced in hook
    }
  };

  return (
    <FundingSectionPage
      title="Bank Match"
      subtitle="Rank banks, credit unions, fintech lenders, and specialty providers based on each funding client's profile and readiness."
      activeStage="match"
      actions={(
        <FundingAIAdvisorActionLink
          label="Explain matches"
          clientId={selectedClientId || undefined}
          prompt="Explain lender matches"
        />
      )}
    >
      <div className="grid gap-4 xl:grid-cols-[320px_1fr]">
        <FundingPanel title="Funding client" subtitle="Select a client to review match signals">
          <FundingMatchClientSelector
            clients={clients}
            selectedClientId={selectedClientId}
            onSelect={setSelectedClientId}
            loading={clientsLoading}
            requiresAgencySelection={requiresAgencySelection}
          />
        </FundingPanel>

        <div className="space-y-4">
          <FundingPanel title="Client summary" subtitle="Profile signals used for lender matching">
            <FundingMatchRunPanel
              client={selectedClient}
              onRunMatch={handleRunMatch}
              running={running}
            />
          </FundingPanel>

          <FundingPanel title="Match filters" subtitle="Filter recommended lender and product fits">
            <FundingMatchFilters
              lenderType={lenderType}
              productType={productType}
              fitTier={fitTier}
              onLenderTypeChange={setLenderType}
              onProductTypeChange={setProductType}
              onFitTierChange={setFitTier}
            />
          </FundingPanel>

          <FundingPanel
            title="Recommended matches"
            subtitle="Estimated lender and product fits ranked by match score"
          >
            {matchError ? (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
                {matchError}
              </div>
            ) : null}

            {matchesLoading && !matches.length ? (
              <p className="text-sm text-[var(--fg-muted)]">Loading matches...</p>
            ) : null}

            {!matchesLoading && !matches.length ? (
              <p className="text-sm text-[var(--fg-muted)]">
                Run match to generate recommended lender and product fits for this client.
              </p>
            ) : (
              <div className="space-y-4">
                {matches.map((match) => (
                  <FundingMatchCard
                    key={match.id}
                    match={match}
                    agencyId={agencyIdForClient}
                    onAddToSequence={handleAddToSequence}
                    onCreateSubmission={handleCreateSubmission}
                    adding={addingMatchId === match.id}
                    creatingSubmission={creatingSubmissionMatchId === match.id}
                  />
                ))}
              </div>
            )}
          </FundingPanel>
        </div>
      </div>
    </FundingSectionPage>
  );
};

export default FundingBankMatchPage;
